import React from 'react';
import { useLocale } from 'locale';

import { SliderRange } from 'ds/components/slider';
import {
  Section,
  SectionBody,
  PriceWrapper,
  SectionHeader,
} from 'components/filters/styled/common';

import FilterSection from 'components/filters/common/DropdownSection';
import { GenericFilterProps } from 'components/filters/types';

import { isFilterActiveByType } from 'components/filters/utils';
import { DealType } from 'utils/entities';
import RangeInputs from 'ds/components/input/RangeInputs';
import { SpacingBlock } from 'components/spacing-block';
import { useScreenBreakpoint } from 'consts/breakpoints';


interface PriceRangeProps extends GenericFilterProps<'priceRange'> {}

const PriceRange: React.FunctionComponent<PriceRangeProps> = ({
  alignment,
  resetValue,
  commitValues,
  commitedValue,
  initialValue,
  setIntermediateValue,
  intermediateValue,
  config,
  dealType,
  type,
  isInline,
}) => {
  const { t, formatMoneyWithMetricPrefix } = useLocale();
  const { budgetByDealType, filtersIcons } = config;

  const isCombinedFiltersSearchView = useScreenBreakpoint() < 3;

  const [ commitedFrom, commitedTo ] = commitedValue;
  const [ initialFrom, initialTo ] = initialValue;

  const isActive = isFilterActiveByType.priceRange(commitedValue, initialValue);
  const valuesAreInitial = !isFilterActiveByType.priceRange(intermediateValue, initialValue);

  const formatShortLabel = (label: number) => {
    if (dealType === DealType.Buy) return formatMoneyWithMetricPrefix(label, 6, 1E6);
    return formatMoneyWithMetricPrefix(label);
  };

  const txtShortValue = t('filters.priceShortValue', {
    isActive,
    priceFrom: initialFrom === commitedFrom ? null : formatShortLabel(commitedFrom),
    priceTo: initialTo === commitedTo ? null : formatShortLabel(commitedTo),
  });

  const slider = (
    <SliderRange
      {...budgetByDealType[dealType].budgetSliderProps}
      values={intermediateValue}
      onChange={value => setIntermediateValue(value.map(Math.abs) as [number, number])}
      mode={isCombinedFiltersSearchView ? 'black' : 'default'}
    />
  );

  const inputs = (
    <RangeInputs
      {...budgetByDealType[dealType].budgetSliderProps}
      value={intermediateValue}
      onChange={value => setIntermediateValue(value)}
      mode={isCombinedFiltersSearchView ? 'black' : 'default'}
      isMoneyMode
    />
  );

  if (isInline) {
    return (
      <Section combinedFiltersSearchMode={isCombinedFiltersSearchView} data-section-filter-name={type} data-auto={`filter-${type}`}>
        <SectionHeader isInline combinedFiltersSearchMode={isCombinedFiltersSearchView}>
          {t('filters.priceRange.secondaryTitle')}
        </SectionHeader >
        <SectionBody combinedFiltersSearchMode={isCombinedFiltersSearchView} data-auto="filterCheckboxes">
          {isCombinedFiltersSearchView ? (
            <>
              {inputs}
              <SpacingBlock mTop={3} />
              <PriceWrapper>
                {slider}
              </PriceWrapper>
            </>
          ) : (
            <>
              <PriceWrapper>
                {slider}
              </PriceWrapper>
              {inputs}
            </>
          )}
        </SectionBody>
      </Section>
    );
  }

  return (
    <FilterSection
      isClearButtonDisabled={valuesAreInitial}
      type={type}
      alignment={alignment}
      title={t('filters.priceRange.secondaryTitle')}
      icon={filtersIcons[type]}
      onClear={resetValue}
      label={txtShortValue}
      onSubmit={commitValues}
      isActive={isActive}
    >
      {slider}
      {inputs}
    </FilterSection>
  );
};

export default React.memo(PriceRange);
